import type { LucideIcon } from 'lucide-react';
import { GripVertical, Trash2, Undo2 } from 'lucide-react';
import React, { useContext, useState } from 'react';
import { AutomationEditorContext } from './AutomationEditorContext';
import { DeleteActionConfirmDialog } from './DeleteActionConfirmDialog';
import styles from './EditAutomationTemplate.module.css';

const { actionStep, unsaved, setupRequired, dot, undoButton, dragging } = styles;

interface StepProps {
  index: number;
  icon: LucideIcon;
  title: string;
  subtitle: string;
  needsSetup?: boolean;
  onConfigure: () => void;
  children?: React.ReactNode;
}

export const Step: React.FC<StepProps> = ({
  index,
  icon: Icon,
  title,
  subtitle,
  needsSetup = false,
  onConfigure,
  children,
}) => {
  const { editor } = useContext(AutomationEditorContext)!;
  const { state, deleteAction, undoAction } = editor;
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  const isDirty = state.dirtySteps.actions.has(index);

  let badge: React.ReactNode = null;
  let stateClass = '';

  if (isDirty) {
    badge = (
      <>
        <mark data-type="unsaved">Unsaved changes</mark>
        <button
          type="button"
          onClick={() => undoAction(index)}
          className={undoButton}
          aria-label="Undo changes"
          title="Undo changes">
          <Undo2 />
        </button>
      </>
    );
    stateClass = unsaved;
  } else if (needsSetup) {
    badge = <mark data-type="setup">Setup required</mark>;
    stateClass = setupRequired;
  }

  const handleDelete = () => {
    deleteAction(index);
  };

  const handleDragStart = (e: React.DragEvent) => {
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', index.toString());
    setIsDragging(true);
  };

  const handleDragEnd = () => {
    setIsDragging(false);
  };

  return (
    <>
      <article className={`${actionStep} ${stateClass} ${isDragging ? dragging : ''}`}>
        <span className={dot} />
        <hgroup>
          <h3>
            <Icon />
            {title}
            {badge}
          </h3>
          <p>{subtitle}</p>
        </hgroup>
        {children}
        <button onClick={onConfigure} type="button">
          Configure →
        </button>
        <aside>
          <button
            type="button"
            onClick={() => setShowDeleteConfirm(true)}
            aria-label="Delete step"
            data-action="delete">
            <Trash2 />
          </button>
          <button
            type="button"
            aria-label="Drag to reorder"
            data-action="drag"
            draggable
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}>
            <GripVertical />
          </button>
        </aside>
      </article>
      <DeleteActionConfirmDialog
        open={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleDelete}
        actionTitle={title}
      />
    </>
  );
};
